// TODO: If you make edits to this file you must deal with these ESLint rules
/*eslint-disable @typescript-eslint/explicit-module-boundary-types */

import React from 'react';
import { useUIDSeed } from 'react-uid';

import { useFormik } from 'formik';
import { camelCase } from 'lodash';
import * as Yup from 'yup';

import { useSendBrokerInterestMutationResponse as ResponseData } from '@zego-types/useSendBrokerInterestMutation.graphql';
import { FormContext } from '@zego/forms/Form';
import FormActions from '@zego/forms/FormActions';
import FormField from '@zego/forms/FormField';
import FormFieldGroup from '@zego/forms/FormFieldGroup';
import Label from '@zego/forms/Label';
import SegmentedChoiceInput from '@zego/forms/SegmentedChoiceInput';
import useSendBrokerInterestMutation from '@zego/mutations/useSendBrokerInterestMutation';
import { BREAKPOINTS, disabledColor, primaryColor, unit } from '@zego/theme';
import { useTracking } from '@zego/tracking';

import Button from '@zego/components/atoms/Button';
import CurrencyInput from '@zego/components/atoms/CurrencyInput';
import Input from '@zego/components/atoms/Input';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import WarningInput from '@zego/components/atoms/WarningInput';

const initialValues = () => ({
  brokerageName: '',
  firstNames: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  fleetsManaged: '',
  worksWithFleets: '',
  estimatedTotalGwp: '',
  estimatedFleetGwp: '',
  estimatedLeadsPerMonth: '',
});

const gwpValueGreaterThanWarningThreshold = (v: number): boolean => {
  return v > 999999999999;
};

const leadsValueGreaterThanWarningThreshold = (v: number): boolean => {
  return v > 9999;
};

// Also allows accented characters and whitespace for multiple given names
// See: https://stackoverflow.com/a/26900132
const textOnlyRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ ]+$/;

const validationSchema = Yup.object().shape({
  brokerageName: Yup.string().required('Required'),
  firstNames: Yup.string()
    .matches(textOnlyRegex, 'Please enter valid first name(s).')
    .required('Required'),
  lastName: Yup.string()
    .matches(textOnlyRegex, 'Please enter a valid last name.')
    .required('Required'),
  email: Yup.string().email('Please enter a valid email').required('Required'),
  phoneNumber: Yup.string().required('Required'),
  fleetsManaged: Yup.string().required('Required'),
  worksWithFleets: Yup.boolean().required('Required'),
  estimatedTotalGwp: Yup.number()
    .min(1, 'Enter amount greater than 0.')
    .required('Required'),
  estimatedFleetGwp: Yup.number()
    .min(1, 'Enter amount greater than 0.')
    .required('Required'),
  estimatedLeadsPerMonth: Yup.number()
    .min(1, 'Enter number greater than 0.')
    .required('Required'),
});

const StyledLabel = ({ children }): JSX.Element => (
  <Label
    css={{
      display: 'block',
      color: disabledColor(10),
      margin: `8px auto`,
    }}
  >
    {children}
  </Label>
);

function Form(props) {
  const seed = useUIDSeed();

  return (
    <FormContext.Provider value={seed}>
      <form {...props} />
    </FormContext.Provider>
  );
}

const getCamelCase = (str: string): string => camelCase(str);

const BrokerInterestForm = ({ onSuccess, id }) => {
  const { call: sendBrokerInterestMutation } = useSendBrokerInterestMutation(
    {},
  );

  const { trackEvent } = useTracking();

  const handleSubmit = (values, { setErrors, setSubmitting }) => {
    values.country = 'GB';

    // worksWithFleets is a 'true'/'false' radio string; the schema declares it
    // Boolean!, so cast it to a real boolean for the mutation input.
    const input = {
      ...values,
      worksWithFleets: values.worksWithFleets === 'true',
    };

    sendBrokerInterestMutation(
      {
        variables: { input },
      },
      (error, data: ResponseData) => {
        if (error) console.error(error);
        if (
          data?.sendBrokerInterest.__typename === 'SendBrokerInterestSuccess'
        ) {
          onSuccess(values.brokerageName);
        } else if (
          data.sendBrokerInterest.__typename === 'SendBrokerInterestError' &&
          data.sendBrokerInterest.fieldErrors
        ) {
          const { fieldErrors } = data.sendBrokerInterest;
          const errors = fieldErrors.reduce((acc, next) => {
            acc[getCamelCase(next.fieldName)] = next.errors;
            return acc;
          }, {});
          setErrors(errors);
          setSubmitting(false);
        }
      },
    );
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <Form onSubmit={formik.handleSubmit} id={id}>
      <Spacer height={unit(6)} />
      <FormField
        name="brokerageName"
        label="Brokerage name"
        placeholder="Zego"
        widget={Input}
        value={formik.values.brokerageName}
        error={formik.errors.brokerageName}
        onChange={event =>
          formik.setFieldValue('brokerageName', event.target.value, false)
        }
      />
      <Spacer height={unit(3)} />
      <FormFieldGroup spacing={3}>
        <View
          css={{
            [BREAKPOINTS.medium]: {
              display: 'flex',
            },
          }}
        >
          <FormField
            css={{
              marginBottom: unit(3),
              [BREAKPOINTS.medium]: {
                marginBottom: 0,
              },
            }}
            name="firstNames"
            label="First name(s)"
            placeholder="John"
            widget={Input}
            value={formik.values.firstNames}
            error={formik.errors.firstNames}
            onChange={event =>
              formik.setFieldValue('firstNames', event.target.value, false)
            }
          />
          <Spacer width={unit(4)} />
          <FormField
            name="lastName"
            label="Last name"
            placeholder="Smith"
            widget={Input}
            value={formik.values.lastName}
            error={formik.errors.lastName}
            onChange={event =>
              formik.setFieldValue('lastName', event.target.value, false)
            }
          />
        </View>
      </FormFieldGroup>
      <Spacer height={unit(3)} />
      <FormFieldGroup spacing={3}>
        <View
          css={{
            [BREAKPOINTS.medium]: {
              display: 'flex',
            },
          }}
        >
          <FormField
            css={{
              marginBottom: unit(3),
              [BREAKPOINTS.medium]: {
                marginBottom: 0,
              },
            }}
            name="email"
            label="Email"
            placeholder="john.smith@zego.com"
            widget={Input}
            value={formik.values.email}
            error={formik.errors.email}
            onChange={event =>
              formik.setFieldValue('email', event.target.value, false)
            }
          />
          <Spacer width={unit(4)} />
          <FormField
            name="phoneNumber"
            label="Phone number"
            placeholder="07700 900299"
            widget={Input}
            value={formik.values.phoneNumber}
            error={formik.errors.phoneNumber}
            onChange={event =>
              formik.setFieldValue('phoneNumber', event.target.value, false)
            }
          />
        </View>
      </FormFieldGroup>
      <Spacer height={unit(3)} />
      <FormField
        name="fleetsManaged"
        label="How many fleets do you currently manage?"
        widget={SegmentedChoiceInput}
        widgetProps={{
          choices: [
            { label: '0 - 20', value: '0 - 20' },
            { label: '21 - 100', value: '21 - 100' },
            { label: '100+', value: '100+' },
          ],
        }}
        value={formik.values.fleetsManaged}
        error={formik.errors.fleetsManaged}
        onChange={event =>
          formik.setFieldValue('fleetsManaged', event.target.value, false)
        }
      />
      <Spacer height={unit(3)} />
      <FormField
        name="whoYouWorkWith"
        label="Do you currently work with private hire, courier, trades and/or micromobility fleets?"
        widget={SegmentedChoiceInput}
        widgetProps={{
          choices: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
          ],
        }}
        value={formik.values.worksWithFleets}
        error={formik.errors.worksWithFleets}
        onChange={event =>
          formik.setFieldValue('worksWithFleets', event.target.value, false)
        }
      />
      <Spacer height={unit(3)} />
      <FormFieldGroup spacing={3}>
        <View
          css={{
            [BREAKPOINTS.medium]: {
              display: 'flex',
            },
          }}
        >
          <FormField
            css={{
              marginBottom: unit(3),
              [BREAKPOINTS.medium]: {
                marginBottom: 0,
              },
            }}
            name="estimatedTotalGwp"
            label={
              <StyledLabel>
                Estimated <span css={{ color: primaryColor(9) }}>total</span>{' '}
                GWP (£)
              </StyledLabel>
            }
            placeholder="10,000,000"
            widget={WarningInput}
            widgetProps={{
              customWidget: <CurrencyInput currencyCode="GBP" decimals={0} />,
              validationFn: gwpValueGreaterThanWarningThreshold,
              warningMessage: 'Please check if your entry is correct',
            }}
            value={formik.values.estimatedTotalGwp}
            error={formik.errors.estimatedTotalGwp}
            onChange={value =>
              formik.setFieldValue('estimatedTotalGwp', value, false)
            }
          />
          <Spacer width={unit(4)} />
          <FormField
            name="estimatedFleetGwp"
            label={
              <StyledLabel>
                Estimated <span css={{ color: primaryColor(9) }}>fleet</span>{' '}
                GWP (£)
              </StyledLabel>
            }
            placeholder="5,000,000"
            widget={WarningInput}
            widgetProps={{
              customWidget: <CurrencyInput currencyCode="GBP" decimals={0} />,
              validationFn: gwpValueGreaterThanWarningThreshold,
              warningMessage: 'Please check if your entry is correct',
            }}
            value={formik.values.estimatedFleetGwp}
            error={formik.errors.estimatedFleetGwp}
            onChange={value =>
              formik.setFieldValue('estimatedFleetGwp', value, false)
            }
          />
        </View>
      </FormFieldGroup>
      <Spacer height={unit(3)} />
      <FormFieldGroup>
        <View
          css={{
            [BREAKPOINTS.medium]: {
              display: 'flex',
            },
          }}
        >
          <FormField
            name="estimatedLeadsPerMonth"
            label="Estimated leads per month"
            placeholder="50"
            widget={WarningInput}
            widgetProps={{
              type: 'number',
              validationFn: leadsValueGreaterThanWarningThreshold,
              warningMessage: 'Please check if your entry is correct',
            }}
            value={formik.values.estimatedLeadsPerMonth}
            error={formik.errors.estimatedLeadsPerMonth}
            onChange={value =>
              formik.setFieldValue('estimatedLeadsPerMonth', value, false)
            }
          />
          <Spacer width={unit(4)} />
          <div css={{ width: '100%' }} />
        </View>
      </FormFieldGroup>
      <Spacer height={unit(3)} />
      <FormActions>
        <Button
          type="submit"
          stretch={true}
          disabled={formik.isSubmitting}
          submitting={formik.isSubmitting}
          spinnerColor="#000"
          css={{
            [BREAKPOINTS.medium]: {
              width: '48%',
            },
          }}
          onClick={(): void => {
            trackEvent(
              'Web: B2B Broker Partner With Us Form - submit button clicked',
            );
          }}
        >
          Submit
        </Button>
      </FormActions>
    </Form>
  );
};

export default BrokerInterestForm;
